import { useState } from 'react'
import { buildPoolRules } from '@/lib/poolRules'

/* "How this pool scores" — the terms a member is actually playing under, built
 * from that pool's real settings. See lib/poolRules.ts for why none of it is
 * boilerplate.
 *
 * NON-BLOCKING BY DESIGN. When a member has not accepted the current terms the
 * panel opens itself and asks, but picking is never gated on it. A bug in an
 * acknowledgement gate would lock members out of picking during a live week,
 * which is a far worse failure than someone being surprised by a tie. Disclosure
 * is the goal, not consent-as-a-checkpoint.
 *
 * <details> carries the open/closed behaviour so it stays keyboard-accessible
 * and screen-reader-announced.
 *
 * Open state is DERIVED, not synchronised: it defaults to whatever the
 * acknowledgement needs and is overridden only once the member touches it. That
 * matters because the acknowledgement query resolves after first render, and
 * pushing that into state from an effect causes a cascading re-render. The
 * caller passes `key={rulesVersion}`, so changed terms remount this and clear
 * any previous collapse.
 */
export default function PoolRules({
  tiePolicy,
  lockEnabled,
  tiebreakerName,
  tiebreakerDescription,
  needsAcknowledgement = false,
  onAcknowledge,
  isAcknowledging = false,
  acknowledgeError,
}: {
  tiePolicy: string
  lockEnabled: boolean
  tiebreakerName?: string
  tiebreakerDescription?: string
  needsAcknowledgement?: boolean
  onAcknowledge?: () => void
  isAcknowledging?: boolean
  acknowledgeError?: Error | null
}) {
  // null = member has not touched it, so follow the acknowledgement. Once they
  // toggle, their choice wins — collapsing stays their call.
  const [memberToggled, setMemberToggled] = useState<boolean | null>(null)
  const open = memberToggled ?? needsAcknowledgement

  const rules = buildPoolRules({ tiePolicy, lockEnabled, tiebreakerName, tiebreakerDescription })

  return (
    <details
      open={open}
      onToggle={(event) => setMemberToggled(event.currentTarget.open)}
      className={`bg-card rounded-row mb-5 border ${
        needsAcknowledgement ? 'border-mark border-2' : 'border-rule'
      }`}
    >
      <summary className="text-ink cursor-pointer px-4 py-3 font-mono text-eyebrow tracking-[0.16em] uppercase">
        {needsAcknowledgement ? 'Before you pick · how this pool scores' : 'How this pool scores'}
      </summary>

      <dl className="border-rule border-t px-4 py-3.5">
        {rules.map((rule) => (
          <div key={rule.label} className="mb-3 last:mb-0">
            <dt className="text-mark font-mono text-eyebrow tracking-[0.16em] uppercase">
              {rule.label}
            </dt>
            <dd className="text-ink mt-1 text-body">{rule.body}</dd>
          </div>
        ))}
      </dl>

      {needsAcknowledgement && onAcknowledge && (
        <div className="border-rule bg-field-tint border-t px-4 py-3.5">
          <p className="text-ink text-body">
            These are this pool’s rules for the season. Your picks count either way — this just
            confirms you have seen them.
          </p>
          {acknowledgeError && (
            <p role="alert" className="text-flag mt-2 text-body">
              Couldn’t save that. {acknowledgeError.message}
            </p>
          )}
          <button
            type="button"
            onClick={onAcknowledge}
            disabled={isAcknowledging}
            className="bg-field hover:bg-mark rounded-row mt-3 px-4 py-2.5 text-body font-semibold text-white disabled:opacity-60"
          >
            {isAcknowledging ? 'Saving…' : 'Got it'}
          </button>
        </div>
      )}
    </details>
  )
}
